import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { GlossarySourceCategory } from '../../types/glossary';
import { glossaryCategoryService } from '../../services/glossaryCategoryService';

interface CategorySelectorProps {
  selectedCategoryId: string | null;
  onCategoryChange: (categoryId: string | null) => void;
  className?: string;
}

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  selectedCategoryId,
  onCategoryChange,
  className = ''
}) => {
  const [categories, setCategories] = useState<GlossarySourceCategory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await glossaryCategoryService.getActiveCategories();
      setCategories(data.sort((a: GlossarySourceCategory, b: GlossarySourceCategory) => a.order - b.order));
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (value: string) => {
    if (value === 'all') {
      onCategoryChange(null);
    } else {
      onCategoryChange(value);
    }
  };

  const selectedCategory = categories.find(cat => cat.id === selectedCategoryId);

  if (loading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <span className="text-sm text-gray-500">Loading categories...</span>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <Badge variant="secondary">All Terms</Badge>
      </div>
    );
  }

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <span className="text-sm font-medium text-gray-800 dark:text-white">Category:</span>
      <Select
        value={selectedCategoryId || 'all'}
        onValueChange={handleValueChange}
      >
        <SelectTrigger className="w-[200px] bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white">
          <SelectValue placeholder="Select category">
            {selectedCategory ? selectedCategory.name : 'All Categories'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            <div className="flex items-center space-x-2">
              <span>All Categories</span>
            </div>
          </SelectItem>
          {categories.map((category) => (
            <SelectItem key={category.id} value={category.id!}>
              <div className="flex items-center space-x-2">
                <span>{category.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};